import { createCollectionLink, router } from "@/router";
import { RouteName } from "@/router/routeNameEnum";
import PostHog from "@/services/analytics/postHog";
import { consumeCheckoutAuthIntent } from "@/services/user/auth/login/checkoutAuthIntent";
import { clearAfterAuthRedirect, getAfterAuthRedirect } from "@/services/afterAuthRedirectService";
import { fetchCartStatus, fetchFullCart, mergeAnonymousCart, saveShippingMethod, savePaymentMethod, attachShippingAddressToCart } from "@/services/marketplace/cart/cartApi";
import { CartStatus, type ShoppingCart } from "@/services/marketplace/cart/cartTypes";
import { useCart } from "@/composables/marketplace/cart";
import { useAnonymousIdentifier } from "@/composables/anonymousIdentifier.client";
import { useAnonymousCartPreferences } from "@/composables/marketplace/cart/useAnonymousCartPreferences.client";
import { useCartStatus } from "@/composables/marketplace/cart/useCartStatus";
import { createAddress } from "@/services/addresses/addressApi";
import type { RouteLocationAsPathGeneric, RouteLocationAsRelativeGeneric } from "vue-router";

type Redirect = string | RouteLocationAsPathGeneric | RouteLocationAsRelativeGeneric | undefined;

export const afterLogin = async (): Promise<void> => {
  await mergeAnonymousCartIfExists(); // Must be done before redirect, otherwise cart will be empty
  trackCheckoutAuthCompleted();
  await redirectAfterLogin();
};

const trackCheckoutAuthCompleted = (): void => {
  if (consumeCheckoutAuthIntent()) {
    PostHog.trackEvent(PostHog.EventName.Checkout_AuthCompleted);
  }
};

const mergeAnonymousCartIfExists = async (): Promise<void> => {
  const anonymousIdentifier = useAnonymousIdentifier().getIdentifier();
  const status = await fetchCartStatus(anonymousIdentifier);
  if (status === CartStatus.NotEmpty) {
    const mergedCart = await mergeAnonymousCart(anonymousIdentifier);
    await applyLocalPreferencesToMergedCart(mergedCart);
  }
  useAnonymousCartPreferences().clearPreferences();
  useCartStatus().refresh();

  if (router.currentRoute.value.name === RouteName.MarketplaceOrder) {
    const sellerNickname = router.currentRoute.value.params.sellerNickname as string;
    const { cart } = useCart();
    const sellerId = cart.value?.subcarts.find(({ sellerNickname: nick }) => nick === sellerNickname)?.sellerId;
    if (sellerId !== undefined) {
      cart.value = await fetchFullCart(sellerId);
    }
  }
};

const applyLocalPreferencesToMergedCart = async (mergedCart: ShoppingCart | undefined): Promise<void> => {
  const { selectedShippingMethod, selectedPaymentMethod, zasilkovnaAddress } = useAnonymousCartPreferences();

  const applyAddress = async (): Promise<void> => {
    if (zasilkovnaAddress.value === null || mergedCart?.shippingAddress !== null) return;
    const address = await createAddress(zasilkovnaAddress.value);
    if (mergedCart !== undefined) {
      await attachShippingAddressToCart(mergedCart, address.id);
    }
  };

  const saveShipping = selectedShippingMethod.value !== null && mergedCart?.selectedShippingMethod === null ? saveShippingMethod(selectedShippingMethod.value.id) : Promise.resolve();

  const savePayment = selectedPaymentMethod.value !== null && mergedCart?.selectedPaymentMethod === null ? savePaymentMethod(selectedPaymentMethod.value.id) : Promise.resolve();

  await Promise.all([saveShipping, savePayment, applyAddress()]);
};

const redirectAfterLogin = async (): Promise<Redirect> => {
  let redirect;

  const pendingRedirect = await getAfterAuthRedirect();
  if (pendingRedirect !== undefined) {
    clearAfterAuthRedirect();
    redirect = pendingRedirect;
  } else {
    const route = router.currentRoute.value;
    const queryRedirect = route.query.redirect_url;
    const isValidRedirect = typeof queryRedirect === "string";
    if (route.name === undefined) {
      return undefined; // First load
    } else if (isValidRedirect) {
      redirect = queryRedirect;
    } else if (route.meta.redirectWhenLoggedIn) {
      redirect = route.meta.redirectWhenLoggedIn;
    } else if (route.meta.public) {
      return undefined;
    } else {
      redirect = createCollectionLink();
    }
  }

  if (redirect !== undefined) {
    router.push(redirect);
  }
};
